import { FolderCopyOutlined, IosShareRounded } from "@mui/icons-material";
import { Button, Skeleton, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router";
import { useTeamPageLayoutContext } from "../TeamPageLayout";
import { Plan, TeamStrategicMapPlaceholder, TeamStrategyFilterButton } from "./components";
import { useTeamStrategySettings } from "./hooks";

const TeamStrategicMapPage = () => {
  const { team } = useTeamPageLayoutContext();
  const navigate = useNavigate();
  const { settings, updateSettings } = useTeamStrategySettings(team.slug);

  const hasEditPermission = team.isAdministrator || team.isMember;

  if (team.planGuid != null) {
    return (
      <Plan
        teamName={team.name}
        teamSlug={team.slug}
        planGuid={team.planGuid}
        canEdit={hasEditPermission}
        tableView={settings.tableView}
        onViewToggled={(tableView) => updateSettings({ tableView })}
        filters={settings.filters}
        headerContent={({
          isLoading,
          isPastView,
          handleVersionsClicked,
          handleExportClicked,
          plan,
          dateTimeLocal,
          dateTimeLocalUtcOffsetInMinutes,
        }) => (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", justifyContent: "space-between", width: "100%" }}
          >
            <Stack spacing={0.25}>
              <Typography variant="h5">Strategic Map</Typography>
              {isLoading ? (
                <Skeleton variant="rectangular" height={25} width={350} />
              ) : !isPastView ? (
                plan && (
                  <Typography
                    variant="body1"
                    title={`Last changed by ${plan.lastModifiedByDisplayName} on ${DateTime.fromISO(
                      plan.lastModifiedUtc,
                      { zone: "utc" },
                    )
                      .toLocal()
                      .toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}`}
                  >
                    {`Last changed by ${plan.lastModifiedByDisplayName} on ${DateTime.fromISO(plan.lastModifiedUtc, {
                      zone: "utc",
                    })
                      .toLocal()
                      .toLocaleString(DateTime.DATE_FULL)}`}
                  </Typography>
                )
              ) : (
                dateTimeLocal && (
                  <Typography>{`Version on ${DateTime.fromISO(dateTimeLocal).toLocaleString(
                    DateTime.DATE_FULL,
                  )}`}</Typography>
                )
              )}
              <Stack direction="row" spacing={0.5} sx={{ pt: 0.5 }}>
                <Button variant="tertiary" startIcon={<FolderCopyOutlined />} onClick={handleVersionsClicked}>
                  Versions
                </Button>

                {!isPastView && (
                  <>
                    <TeamStrategyFilterButton
                      planGuid={team.planGuid!}
                      dateTimeLocal={dateTimeLocal}
                      dateTimeLocalUtcOffsetInMinutes={dateTimeLocalUtcOffsetInMinutes}
                      filters={settings.filters}
                      onFiltersChanged={(filters) => updateSettings({ filters })}
                    />

                    <Button
                      variant="tertiary"
                      startIcon={<IosShareRounded />}
                      onClick={() => handleExportClicked(`${team.slug}-strategic-map-${DateTime.now().toISODate()}`)}
                    >
                      Export
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
            {isPastView ? (
              <Button variant="contained" size="small" onClick={() => navigate(".")}>
                Back to Present
              </Button>
            ) : null}
          </Stack>
        )}
      />
    );
  } else {
    return <TeamStrategicMapPlaceholder />;
  }
};

export { TeamStrategicMapPage };
